import { useState, useEffect } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { Table } from "@cofano/cck";
import { Collapse } from "@mui/material";
import { TableId, useFrontendTable } from "components/table";
import { CollapseButton } from "./Declarations";
import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission } from "imports/Utils";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { LocationLink, ProductLink } from "utils/LinkUtils";
import { SpotSalesCharter } from "types/index";
import { StatusType } from "__generated__/graphql";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";

const CharterCargos = ({ charter, loading, saleCharterRequestId, refetch }: { charter: SpotSalesCharter, loading: boolean, saleCharterRequestId: number, refetch: () => void }) => {

	const session = useStoreState((store) => store.session);

	const [ collapsed, collapse ] = useState(false);

	const { tableProps, refetch: tableRefetch } = useFrontendTable({
		id: TableId.DeclarationCharterCargos,
		content: charter.cargos,
		paginationEnabled: false,
	});

	useEffect(() => {
		tableRefetch(0);
	}, [ loading ]);

	return (
		<div>
			<CollapseButton collapsed={collapsed} onClick={() => collapse(!collapsed)} vertical />

			<Collapse in={!collapsed} timeout="auto" unmountOnExit>
				<Table
					{...tableProps}
					key={saleCharterRequestId}
					contentLoading={loading}
					columns={[ {
						id: "cargoNumber",
						title: intl.formatMessage({ id: "CARGO_NUMBER" }),
						width: 150,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "cargoNumber"),
					}, {
						id: "containerNumber",
						title: intl.formatMessage({ id: "CONTAINER_NUMBER" }),
						disabled: !hasPluginPermission(session, "useContainers"),
					}, {
						id: "containerType",
						title: intl.formatMessage({ id: "CARGO_CONTAINER_TYPE" }),
						render: (row) => DataFormat.containerType(row.containerType),
						disabled: !hasPluginPermission(session, "useContainers"),
					}, {
						id: "lastStatusChange",
						title: intl.formatMessage({ id: "STATUS" }),
						render: row => {
							return (
								<StatusChanger
									session={session}
									entity={row}
									statusType={StatusType.Cargo}
									refetch={refetch} />
							);
						},
						sortable: true,
						sortKey: "lastStatusChange.status.description",
						disabled: !hasPluginPermission(session, "cargoStatuses"),
						width: 175,
					}, {
						id: "product",
						title: intl.formatMessage({ id: "PRODUCT" }),
						render: (row) => <ProductLink product={row.product} />,
						sortable: true,
						sortKey: "product.name",
						disabled: !hasPluginPermission(session, "products"),
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "WEIGHT" }),
						render: (row) => DataFormat.tonnage(row.weight),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "weight"),
					}, {
						id: "volume",
						title: intl.formatMessage({ id: "CARGO_VOLUME" }),
						render: (row) => DataFormat.volumeM3(row.volume),
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "volume"),
					}, {
						id: "teu",
						title: intl.formatMessage({ id: "TEU" }),
						render: (row) =>  DataFormat.simpleUnit(row.teu, DataFormat.UNITS.TEU),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "teu"),
						defaultVisible: false,
					}, {
						id: "firstLoadingAction.location",
						title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
						render: (row) => <LocationLink location={row.firstLoadingAction?.location} />,
						sortable: true,
						sortKey: "firstLoadingAction.location.description",
					}, {
						id: "firstLoadingAction.start",
						title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
						render: (row) => <CargoActionDate action={row.firstLoadingAction} withoutYear />,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
					}, {
						id: "lastDischargingAction.location",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
						render: (row) => <LocationLink location={row.lastDischargingAction?.location} />,
						sortable: true,
						sortKey: "lastDischargingAction.location.description",
					}, {
						id: "lastDischargingAction.end",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
						render: (row) => <CargoActionDate action={row.lastDischargingAction} withoutYear />,
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
					} ]} />
			</Collapse>
		</div>
	);
};

export default CharterCargos;
